/**
 * Valida un email sin regex con cuantificadores anidados (evita ReDoS).
 * No es un validador RFC 5322 completo — alcanza para la UI de captura.
 */
export function isValidEmail(s: string): boolean {
  if (!s) return false;
  if (/\s/.test(s)) return false;
  const at = s.indexOf('@');
  if (at <= 0 || at !== s.lastIndexOf('@')) return false;
  const domain = s.slice(at + 1);
  if (!domain) return false;
  const dot = domain.lastIndexOf('.');
  return dot > 0 && dot < domain.length - 1;
}

/**
 * Telefonía Argentina: el código de área + número de abonado suman 10
 * dígitos en total (CABA: 11 + 8 dígitos, Córdoba: 351 + 7, localidades
 * chicas: 4 + 6). Prefijos típicos que limpiamos al pegar:
 *  - "+54 9 11 4234-5678" → "1142345678"  (descarta +, 54, 9, espacios, guiones)
 *  - "0 11 15 4234 5678"  → "1142345678"  (descarta 0, 15)
 *  - "(011) 4234-5678"    → "01142345678" (paréntesis fuera; el "0" se quita después)
 */

/** Saca todo lo que no sea dígito. */
function soloDigitos(s: string): string {
  return s.replace(/\D/g, '');
}

/**
 * Si total > 10 y aparece un bloque "15" entre las posiciones 2-4 (después
 * del área), lo quita. Sirve para inputs locales con el "15" celular
 * intercalado: "011 15 4234 5678" → "0114234 5678" (luego el "0" se quita).
 */
function tryRemoveMiddle15(d: string): string {
  for (let pos = 2; pos <= 4; pos++) {
    if (d.length > pos + 2 && d.slice(pos, pos + 2) === '15') {
      const candidate = d.slice(0, pos) + d.slice(pos + 2);
      if (candidate.length === 10 || candidate.length === 11) return candidate;
    }
  }
  return d;
}

/**
 * Limpia un teléfono pegado y devuelve los 10 dígitos canonicos (área +
 * número) si se puede; null si no se pudo derivar un número argentino de
 * 10 dígitos. NO determina dónde corta el área — solo los 10 dígitos.
 */
export function limpiarTelefonoPegado(raw: string): string | null {
  let d = soloDigitos(raw);
  if (d.length < 8 || d.length > 15) return null;
  // Prefijo país +54 / +549.
  if (d.length === 13 && d.startsWith('549')) d = d.slice(3); // +54 9 ...
  else if (d.length === 12 && d.startsWith('54')) d = d.slice(2); // +54 ...
  // Bloque "15" celular en el medio (formato local).
  if (d.length > 10) d = tryRemoveMiddle15(d);
  // Prefijos restantes en formato local.
  if (d.length === 11 && d.startsWith('549')) d = d.slice(3);
  else if (d.length === 11 && d.startsWith('15')) d = d.slice(2);
  else if (d.length === 11 && d.startsWith('9')) d = d.slice(1);
  else if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
  if (d.length !== 10) return null;
  return d;
}

/**
 * Si los 2 inputs del form ya son razonables (área 2-4, número 6-8, suman
 * 10), los devuelve normalizados. Si NO suman 10, intenta parsear el campo
 * `numero` como teléfono completo y volver a separar (caso "el usuario
 * pegó el número entero en el campo Celular").
 *
 * No falla si los inputs están vacíos — solo intenta normalizar.
 */
export function normalizarTelefonoArea(
  areaRaw: string,
  numeroRaw: string,
): { area: string; numero: string } {
  const area = soloDigitos(areaRaw).replace(/^0+/, '');
  let numero = soloDigitos(numeroRaw);
  // Caso clásico: número de 10 dígitos con "15" prefijado.
  if (numero.length === 10 && numero.startsWith('15')) numero = numero.slice(2);
  // Si los inputs ya suman 10, listo.
  if (area.length + numero.length === 10) return { area, numero };
  // Caso pegado: usuario pegó el teléfono completo en uno de los 2 campos.
  // Probar concatenar e intentar limpiar.
  const concatenado = area + numero;
  const cleaned = limpiarTelefonoPegado(concatenado);
  if (cleaned) {
    // Heurística de split: preferimos áreas más largas. Si la primera "11"
    // matchea (CABA), tomarla; sino probar 4, 3, 2 dígitos.
    const candidatos: Array<[number, string]> = [
      [2, cleaned.slice(0, 2)],
      [3, cleaned.slice(0, 3)],
      [4, cleaned.slice(0, 4)],
    ];
    // Preferimos área 2 cuando empieza con "11" (CABA es muy común).
    if (cleaned.startsWith('11')) return { area: '11', numero: cleaned.slice(2) };
    // Sino el área original como pista del usuario.
    const [, areaProb] = candidatos.find(([len]) => area.length === len) ?? [3, cleaned.slice(0, 3)];
    return { area: areaProb, numero: cleaned.slice(areaProb.length) };
  }
  return { area, numero };
}

export interface CelularValidacionResultado {
  ok: boolean;
  errorArea?: string;
  errorNumero?: string;
  errorGenerico?: string;
}

/** Reglas básicas por campo (presencia + formato + prefijos). Cualquier
 *  resultado no-null cortocircuita la validación principal. */
function chequeoBasicoCelular(a: string, n: string): CelularValidacionResultado | null {
  if (!a && !n) return { ok: true };
  if (!a) return { ok: false, errorArea: 'Falta el código de área' };
  if (!n) return { ok: false, errorNumero: 'Falta el número' };
  if (!/^\d+$/.test(a)) return { ok: false, errorArea: 'Solo dígitos' };
  if (!/^\d+$/.test(n)) return { ok: false, errorNumero: 'Solo dígitos' };
  if (a.startsWith('0')) return { ok: false, errorArea: 'Sin "0" inicial' };
  if (n.startsWith('15')) return { ok: false, errorNumero: 'Sin "15" inicial' };
  if (a.length < 2 || a.length > 4) {
    return { ok: false, errorArea: 'El área debe tener 2, 3 o 4 dígitos' };
  }
  return null;
}

/** Mensaje de error cuando área+número no suman 10 dígitos. */
function errorSumaInvalida(totalLen: number): CelularValidacionResultado {
  const faltan = 10 - totalLen;
  const cantidad = Math.abs(faltan);
  const verbo = faltan > 0 ? 'Faltan' : 'Sobran';
  const plural = cantidad === 1 ? '' : 's';
  return {
    ok: false,
    errorGenerico: `${verbo} ${cantidad} dígito${plural} (área + número deben sumar 10)`,
  };
}

/**
 * Valida que el titular tenga área + número de celular completos y bien
 * formados. Regla: área 2-4 dígitos sin "0" inicial, número 6-8 dígitos
 * sin "15" inicial, **total = 10 dígitos**. Si la suma no es 10 devolvemos
 * un mensaje único que orienta al usuario.
 */
export function validarCelularTitular(
  area: string,
  numero: string,
): CelularValidacionResultado {
  const a = (area ?? '').trim();
  const n = (numero ?? '').trim();
  const basico = chequeoBasicoCelular(a, n);
  if (basico) return basico;
  if (a.length + n.length !== 10) return errorSumaInvalida(a.length + n.length);
  return { ok: true };
}
